import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAssetPlaybackUrl } from '../data/uploadService';
import EditTree from './EditTree';
import RevisePrompt from './RevisePrompt';
import CropBox from './CropBox';
import { canReviseAsset } from '../utils/nlRevise';
import { CROP_ASPECTS, DEFAULT_CROP_RECT, normalizeCrop } from '../utils/crop';
import { createStillGrabAsset } from '../data/firestore';
import { bust } from '../utils/cacheBust';

// The rendered (object-fit: contain) rect of an image of aspect `ar` inside a
// box of WxH — so a crop overlay can sit exactly on the visible pixels, not the
// letterboxed stage.
function containedBox(W, H, ar) {
  if (!W || !H || !ar) return { left: 0, top: 0, width: W, height: H };
  const boxAR = W / H;
  if (ar > boxAR) { const w = W; const h = W / ar; return { left: 0, top: (H - h) / 2, width: w, height: h }; }
  const h = H; const w = H * ar; return { left: (W - w) / 2, top: 0, width: w, height: h };
}

// Light, fast-to-load URL for quick swipe/arrow browsing (≤3000px magazine).
const bestUrl = (a) =>
  bust(a?.displayUrl || a?.derivatives?.magazine || a?.derivatives?.fulljpeg || a?.thumbUrl || '', a);
// Native-resolution JPEG, loaded on demand once the viewer settles on a shot.
const fullResUrl = (a) => bust(a?.derivatives?.fulljpeg || '', a);
// Lossless full-resolution PNG (bigger) — only fetched when the user asks for it.
const fullPngUrl = (a) => bust(a?.derivatives?.fullpng || '', a);
const fmtMB = (n) => (n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : `${Math.round((n || 0) / 1e6)} MB`);

// Fullscreen, swipeable photo viewer. Fills the viewport in any orientation
// (object-fit: contain), advances on horizontal finger swipe / arrow keys /
// on-screen chevrons, and closes on swipe-down / Esc / the × button.
//
// The image is shown completely bare by default — title, counter and buttons
// only appear when you interact (tap on touch, move the mouse on desktop) and
// fade back out after a couple of idle seconds, so nothing covers the photo.
const CHROME_IDLE_MS = 2500;

export default function PhotoLightbox({
  list, index, onIndex, onClose, onCompare, compareCountFor, onEdit, initialShowTree = false,
}) {
  const count = list.length;
  const asset = list[index];
  const [url, setUrl] = useState(bestUrl(asset));
  const [hqSrc, setHqSrc] = useState(''); // native-res JPEG once loaded
  const [hqState, setHqState] = useState('none'); // 'none' | 'loading' | 'ready'
  const [pngState, setPngState] = useState('none'); // full-res PNG: 'none'|'loading'|'ready'
  const [lowRes, setLowRes] = useState(false); // user chose the fast, lower-res proxy
  const [drag, setDrag] = useState(0); // live horizontal drag offset (px)
  const [dragging, setDragging] = useState(false);
  const [chrome, setChrome] = useState(false); // controls visible?
  const [showTree, setShowTree] = useState(initialShowTree); // edit-tree side panel
  const [showRevise, setShowRevise] = useState(false); // natural-language version panel
  const [reframe, setReframe] = useState(false); // interactive crop/zoom mode
  const [reRect, setReRect] = useState(DEFAULT_CROP_RECT); // normalized crop in DISPLAYED space
  const [reAspect, setReAspect] = useState('free'); // crop aspect id
  const [imgNat, setImgNat] = useState(null); // { w, h } natural dims of the shown image
  const [imgBox, setImgBox] = useState(null); // rendered contain rect within the stage (px)
  const [reSaving, setReSaving] = useState(false);
  const [reMsg, setReMsg] = useState('');
  const [zoom, setZoom] = useState(1); // pinch-zoom scale (1 = fit)
  const [tx, setTx] = useState(0); // pan offset x (px)
  const [ty, setTy] = useState(0); // pan offset y (px)
  const [gesturing, setGesturing] = useState(false); // pinch/pan in progress (kills transition)
  const touch = useRef(null);
  const pinch = useRef(null); // two-finger gesture state
  const pan = useRef(null); // one-finger pan state (only when zoomed)
  const lastTap = useRef(null); // double-tap detection
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const chromeTimer = useRef(null);
  const zoomed = zoom > 1.001;

  // Show the controls and schedule them to auto-hide after a short idle.
  const revealChrome = useCallback(() => {
    setChrome(true);
    if (chromeTimer.current) clearTimeout(chromeTimer.current);
    chromeTimer.current = setTimeout(() => setChrome(false), CHROME_IDLE_MS);
  }, []);
  const hideChrome = useCallback(() => {
    if (chromeTimer.current) clearTimeout(chromeTimer.current);
    setChrome(false);
  }, []);
  useEffect(() => () => { if (chromeTimer.current) clearTimeout(chromeTimer.current); }, []);

  const go = useCallback(
    (d) => onIndex((index + d + count) % count),
    [index, count, onIndex]
  );

  // On demand: fetch the lossless full-resolution PNG and show it in the viewer
  // (max detail, no JPEG artifacts). It's big, so only when the user clicks.
  const loadFullPng = useCallback(() => {
    const p = fullPngUrl(list[index]);
    if (!p) return;
    setPngState('loading');
    const img = new Image();
    img.onload = () => { setHqSrc(p); setPngState('ready'); };
    img.onerror = () => setPngState('none');
    img.src = p;
  }, [list, index]);

  // Resolve the best URL for the current asset (presign the raw bucket only if
  // there's no public derivative yet).
  useEffect(() => {
    let on = true;
    const a = list[index];
    const direct = bestUrl(a);
    if (direct) {
      setUrl(direct);
    } else if (a?.s3Key && !a.isRaw) {
      setUrl('');
      getAssetPlaybackUrl(a.s3Key).then((u) => on && u && setUrl(u)).catch(() => {});
    } else {
      setUrl('');
    }
    return () => { on = false; };
  }, [list, index]);

  // Reset pinch-zoom whenever the viewer moves to a different photo.
  useEffect(() => { setZoom(1); setTx(0); setTy(0); pinch.current = null; pan.current = null; setShowRevise(false); setReframe(false); setReMsg(''); setImgNat(null); }, [index]);

  // Keep the crop overlay glued to the visible pixels: recompute the contained
  // image rect from the stage size + the image's natural aspect whenever we enter
  // reframe mode, the image loads, or the window resizes.
  useEffect(() => {
    if (!reframe) return undefined;
    const recompute = () => {
      const r = stageRef.current?.getBoundingClientRect();
      const ar = imgNat ? imgNat.w / imgNat.h : null;
      if (r && ar) setImgBox(containedBox(r.width, r.height, ar));
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [reframe, imgNat]);

  // Seed a fresh, mostly-full crop each time reframe mode opens.
  useEffect(() => { if (reframe) { setReRect(DEFAULT_CROP_RECT); setReAspect('free'); setReMsg(''); } }, [reframe]);

  // Photos DEFAULT to their max resolution: as soon as the viewer settles on a
  // shot we load the native-resolution JPEG and show it. The initial `url` (a
  // ≤3000px proxy) is only a placeholder so the frame paints instantly and so
  // rapid swiping doesn't fetch every heavy full-res frame — the short settle
  // timer is cancelled on each swipe. The user can opt into the proxy for good
  // with the "Lower res" toggle (`lowRes`), which skips the full-res fetch.
  useEffect(() => {
    setHqSrc('');
    setPngState('none');
    const a = list[index];
    const full = fullResUrl(a);
    if (!full || lowRes) { setHqState('none'); return undefined; }
    setHqState('loading');
    // Upscaled photos load full-res instantly; others get a brief settle so a
    // fast swipe-through doesn't pull every heavy frame, then still land on max res.
    const delay = a?.upscale?.applied ? 0 : 250;
    const t = setTimeout(() => {
      const img = new Image();
      img.onload = () => { setHqSrc(full); setHqState('ready'); };
      img.onerror = () => setHqState('none');
      img.src = full;
    }, delay);
    return () => clearTimeout(t);
  }, [list, index, lowRes]);

  // Keyboard nav + lock background scroll while open.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [go, onClose]);

  // Preload the immediate neighbours so swipes feel instant.
  useEffect(() => {
    if (count < 2) return;
    [1, -1].forEach((d) => {
      const u = bestUrl(list[(index + d + count) % count]);
      if (u) { const img = new Image(); img.src = u; }
    });
  }, [list, index, count]);

  // --- touch gestures: swipe (fit) / pinch-zoom / pan + double-tap zoom ------
  const ZOOM_MAX = 4;
  const fingerDist = (ts) => Math.hypot(ts[0].clientX - ts[1].clientX, ts[0].clientY - ts[1].clientY);
  // Focal point of a touch (or midpoint) relative to the stage CENTRE, in px —
  // the transform is centre-origin so we zoom/pan around this point.
  const focal = (cx, cy) => {
    const r = stageRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return { x: cx - (r.left + r.width / 2), y: cy - (r.top + r.height / 2) };
  };
  // Keep the zoomed image from sliding entirely off-screen.
  const clampPan = (z, x, y) => {
    const r = stageRef.current?.getBoundingClientRect();
    if (!r) return { x, y };
    const maxX = (r.width * (z - 1)) / 2;
    const maxY = (r.height * (z - 1)) / 2;
    return { x: Math.max(-maxX, Math.min(maxX, x)), y: Math.max(-maxY, Math.min(maxY, y)) };
  };

  const onTouchStart = (e) => {
    if (reframe) return; // the crop box owns pointer events in reframe mode
    if (e.touches.length === 2) {
      // Begin a pinch — cancel any in-progress swipe/pan.
      const m = { x: (e.touches[0].clientX + e.touches[1].clientX) / 2, y: (e.touches[0].clientY + e.touches[1].clientY) / 2 };
      const f = focal(m.x, m.y);
      pinch.current = { startDist: fingerDist(e.touches), startZoom: zoom, startTx: tx, startTy: ty, fx: f.x, fy: f.y };
      touch.current = null;
      pan.current = null;
      setDragging(false);
      setDrag(0);
      setGesturing(true);
      return;
    }
    if (e.touches.length !== 1) return;
    const p = e.touches[0];
    if (zoomed) {
      pan.current = { x: p.clientX, y: p.clientY, startTx: tx, startTy: ty };
      setGesturing(true);
      return;
    }
    touch.current = { x: p.clientX, y: p.clientY, dir: null };
  };

  const onTouchMove = (e) => {
    if (reframe) return;
    if (pinch.current && e.touches.length >= 2) {
      const pc = pinch.current;
      const z = Math.max(1, Math.min(ZOOM_MAX, (pc.startZoom * fingerDist(e.touches)) / pc.startDist));
      const k = z / pc.startZoom;
      const c = clampPan(z, pc.fx - (pc.fx - pc.startTx) * k, pc.fy - (pc.fy - pc.startTy) * k);
      setZoom(z); setTx(c.x); setTy(c.y);
      return;
    }
    if (pan.current && e.touches.length === 1) {
      const p = e.touches[0];
      const c = clampPan(zoom, pan.current.startTx + (p.clientX - pan.current.x), pan.current.startTy + (p.clientY - pan.current.y));
      setTx(c.x); setTy(c.y);
      return;
    }
    const t = touch.current;
    if (!t || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - t.x;
    const dy = e.touches[0].clientY - t.y;
    if (!t.dir && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      t.dir = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    }
    if (t.dir === 'h') {
      setDragging(true);
      setDrag(dx);
    }
  };

  // Double-tap toggles between fit and ~2.5x, zoomed around the tap point.
  const handleDoubleTap = (clientX, clientY) => {
    setGesturing(false); // let the transform animate
    if (zoomed) { setZoom(1); setTx(0); setTy(0); return; }
    const z = 2.5;
    const f = focal(clientX, clientY);
    const c = clampPan(z, f.x * (1 - z), f.y * (1 - z));
    setZoom(z); setTx(c.x); setTy(c.y);
  };

  const onTouchEnd = (e) => {
    if (reframe) return;
    if (pinch.current) {
      pinch.current = null;
      setGesturing(false);
      if (zoom <= 1.05) { setZoom(1); setTx(0); setTy(0); } // snap back to fit
      return;
    }
    if (pan.current) {
      const ps = pan.current;
      pan.current = null;
      setGesturing(false);
      // A pan that didn't actually move is a tap — allow double-tap to zoom out.
      const tp = e.changedTouches[0];
      if (tp && Math.abs(tp.clientX - ps.x) < 10 && Math.abs(tp.clientY - ps.y) < 10) {
        const now = Date.now();
        const lt = lastTap.current;
        if (lt && now - lt.t < 300 && Math.hypot(tp.clientX - lt.x, tp.clientY - lt.y) < 40) {
          lastTap.current = null;
          handleDoubleTap(tp.clientX, tp.clientY);
        } else {
          lastTap.current = { t: now, x: tp.clientX, y: tp.clientY };
        }
      }
      return;
    }
    const t = touch.current;
    touch.current = null;
    setDragging(false);
    setDrag(0);
    if (!t) return;
    const end = e.changedTouches[0];
    if (!end) return;
    const dx = end.clientX - t.x;
    const dy = end.clientY - t.y;

    // Tap (no real movement, no swipe direction) → check for a double-tap.
    if (!t.dir && Math.abs(dx) < 10 && Math.abs(dy) < 10) {
      const now = Date.now();
      const lt = lastTap.current;
      if (lt && now - lt.t < 300 && Math.hypot(end.clientX - lt.x, end.clientY - lt.y) < 40) {
        lastTap.current = null;
        handleDoubleTap(end.clientX, end.clientY);
        return;
      }
      lastTap.current = { t: now, x: end.clientX, y: end.clientY };
      return;
    }

    // Swipe nav / swipe-to-close only when at fit scale (zoomed → pan instead).
    if (zoomed) return;
    if (t.dir === 'v' && dy > 90 && Math.abs(dy) > Math.abs(dx)) { onClose(); return; }
    if (t.dir === 'h' && count > 1) {
      const threshold = Math.min(110, window.innerWidth * 0.16);
      if (dx <= -threshold) go(1);
      else if (dx >= threshold) go(-1);
    }
  };

  const toggleNativeFullscreen = () => {
    const el = rootRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el.requestFullscreen?.().catch(() => {});
  };

  if (!asset) return null;
  const compareCount = compareCountFor ? compareCountFor(asset) : 0;
  const stop = (e) => e.stopPropagation();

  // Aspect lock for the crop box, in DISPLAYED-normalized space (w/h). 'source'
  // keeps the frame's current shape (== 1 in normalized space); 'free' is null.
  const displayedAR = imgNat ? imgNat.w / imgNat.h : null;
  const aspectLock = (() => {
    if (reAspect === 'free' || !displayedAR) return null;
    if (reAspect === 'source') return 1;
    const a = CROP_ASPECTS.find((c) => c.id === reAspect);
    return a && typeof a.r === 'number' ? a.r / displayedAR : null;
  })();

  // Bake the chosen rectangle as a NEW version of this frame (non-destructive),
  // composing over any crop the current version already carries.
  const applyReframe = async () => {
    if (reSaving) return;
    const r = normalizeCrop(reRect);
    if (!r) { setReMsg('Drag a tighter rectangle first.'); return; }
    setReSaving(true);
    setReMsg('');
    try {
      const c = asset.crop && asset.crop.w > 0 ? asset.crop : null;
      const crop = c
        ? { x: c.x + r.x * c.w, y: c.y + r.y * c.h, w: r.w * c.w, h: r.h * c.h }
        : r;
      await createStillGrabAsset(asset.tripId, {
        source: {
          id: asset.lineage?.sourceAssetId || asset.stillGrab?.sourceAssetId || '',
          name: asset.lineage?.sourceName || asset.name || 'clip',
        },
        timeSec: asset.stillGrab?.timeSec ?? asset.lineage?.sourceTimeSec ?? 0,
        frameIndex: Number.isFinite(Number(asset.stillGrab?.frameIndex)) ? Number(asset.stillGrab.frameIndex) : null,
        grade: asset.grade || null,
        applyGrade: true,
        crop,
        redSource: !!(asset.lineage?.sourceIsRaw || asset.isRaw),
        name: `${String(asset.name || 'frame').replace(/\.[^.]+$/, '')} · reframe`,
        tags: ['nl-revise'],
        revisedFromAssetId: asset.id || '',
      });
      setReMsg('✓ Reframed version queued — it’ll appear shortly.');
      setReframe(false);
    } catch (e) {
      setReMsg(`⚠ ${e.message || 'Reframe failed'}`);
    } finally {
      setReSaving(false);
    }
  };

  return (
    <div
      className={`cs-lb${chrome ? ' is-chrome' : ''}`}
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      onMouseMove={(e) => { if (e.movementX || e.movementY) revealChrome(); }}
    >
      <div className="cs-lb-bar" onClick={stop}>
        <div className="cs-lb-title" title={asset.name}>{asset.name}</div>
        {count > 1 && <div className="cs-lb-count">{index + 1} / {count}</div>}
        {hqState !== 'none' && (
          <div className={`cs-lb-hq ${hqState}`} title={hqState === 'ready' ? 'Full resolution loaded' : 'Loading full resolution…'}>
            {hqState === 'ready' ? 'HQ' : 'HQ…'}
          </div>
        )}
        <div className="cs-lb-actions">
          {asset.derivatives?.fulljpeg && (
            <button
              className={`cs-lb-act${lowRes ? '' : ' is-on'}`}
              title={lowRes
                ? `Showing a faster, lower-resolution preview — tap to view full resolution${asset.width ? ` (${asset.width}×${asset.height})` : ''}`
                : `Showing full resolution${asset.width ? ` (${asset.width}×${asset.height})` : ''} — tap for a faster, lower-resolution preview`}
              onClick={() => setLowRes((v) => !v)}
            >
              {lowRes ? '◱ Low-res' : '◲ Full-res'}
            </button>
          )}
          {asset.derivatives?.fulljpeg && (
            <a
              className={`cs-lb-act${asset.upscale?.applied ? ' is-upscaled' : ''}`}
              href={fullResUrl(asset)}
              target="_blank"
              rel="noreferrer"
              title={`Open the full-resolution image${asset.width ? ` (${asset.width}×${asset.height})` : ''} in a new browser tab`}
              onClick={stop}
            >
              {asset.upscale?.applied && asset.width ? `⤴ ${asset.width}×${asset.height}` : '↗ Open'}
            </a>
          )}
          {asset.derivatives?.fullpng && (
            <button
              className={`cs-lb-act${pngState === 'ready' ? ' is-on' : ''}`}
              title={`Load the lossless full-resolution PNG${asset.fullPngBytes ? ` (${fmtMB(asset.fullPngBytes)})` : ''} into the viewer — max detail, no JPEG compression`}
              onClick={(e) => { stop(e); loadFullPng(); }}
              disabled={pngState === 'loading'}
            >
              {pngState === 'loading'
                ? '⤢ PNG…'
                : (pngState === 'ready'
                  ? '⤢ PNG ✓'
                  : `⤢ PNG${asset.fullPngBytes ? ` ${fmtMB(asset.fullPngBytes)}` : ''}`)}
            </button>
          )}
          {compareCount > 1 && (
            <button className="cs-lb-act" title={`Compare ${compareCount} variations`} onClick={() => onCompare(asset)}>
              ⊞ {compareCount}
            </button>
          )}
          {(asset.editManifest?.length > 0 || asset.editTree?.stages?.length > 0 || (asset.tripId && asset.id)) && (
            <button className={`cs-lb-act${showTree ? ' is-on' : ''}`} title="Show details — trace the edit from negative to final" onClick={() => setShowTree((v) => !v)}>🌳</button>
          )}
          {canReviseAsset(asset) && (
            <button className={`cs-lb-act${showRevise ? ' is-on' : ''}`} title="Describe a change → new version of this frame" onClick={() => { setShowRevise((v) => !v); setReframe(false); }}>✨</button>
          )}
          {canReviseAsset(asset) && (
            <button className={`cs-lb-act${reframe ? ' is-on' : ''}`} title="Reframe — drag a rectangle to crop/zoom into a new version" onClick={() => { setReframe((v) => !v); setShowRevise(false); setZoom(1); setTx(0); setTy(0); }}>⤢</button>
          )}
          {onEdit && (
            <button className="cs-lb-act" title="Edit color" onClick={() => onEdit(asset)}>🎨</button>
          )}
          <button className="cs-lb-act cs-lb-fs" title="Toggle device fullscreen" onClick={toggleNativeFullscreen}>⛶</button>
          <button className="cs-lb-act cs-lb-close" title="Close (Esc)" onClick={onClose}>✕</button>
        </div>
      </div>

      {showRevise && (
        <div
          onClick={stop}
          className="cs-lb-revise"
          style={{ position: 'absolute', top: 56, left: '50%', transform: 'translateX(-50%)', width: 'min(560px, 92vw)', zIndex: 6 }}
        >
          <RevisePrompt asset={asset} autoFocus onDone={() => setShowRevise(false)} />
        </div>
      )}

      {showTree && (
        <div
          onClick={stop}
          style={{ position: 'absolute', top: 56, right: 12, bottom: 12, width: 'min(360px, 86vw)', overflowY: 'auto', zIndex: 5 }}
        >
          <EditTree
            key={asset.id}
            editTree={asset.editTree}
            manifest={asset.editManifest}
            editTreeStatus={asset.editTreeStatus}
            tripId={asset.tripId}
            assetId={asset.id}
            defaultOpen
          />
        </div>
      )}

      <div
        className="cs-lb-stage"
        ref={stageRef}
        style={{ touchAction: 'none' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={() => { if (reframe) return; (chrome ? hideChrome() : revealChrome()); }}
      >
        {count > 1 && !reframe && (
          <button
            className="cs-lb-nav prev"
            title="Previous (←)"
            onClick={(e) => { stop(e); revealChrome(); go(-1); }}
            aria-label="Previous"
          >‹</button>
        )}
        {url ? (
          <img
            key={asset.id}
            className="cs-lb-img"
            src={lowRes ? url : (hqSrc || url)}
            alt=""
            draggable={false}
            onLoad={(e) => setImgNat({ w: e.target.naturalWidth || 0, h: e.target.naturalHeight || 0 })}
            style={{
              transform: reframe
                ? 'none'
                : (zoomed ? `translate(${tx}px, ${ty}px) scale(${zoom})` : `translateX(${drag}px)`),
              transition: dragging || gesturing ? 'none' : 'transform 0.22s ease',
              transformOrigin: 'center center',
              willChange: 'transform',
            }}
          />
        ) : (
          <div className="cs-lb-ph">rendering…</div>
        )}
        {reframe && imgBox && (
          <div
            className="cs-lb-cropwrap"
            onClick={stop}
            style={{ position: 'absolute', left: imgBox.left, top: imgBox.top, width: imgBox.width, height: imgBox.height, zIndex: 4 }}
          >
            <CropBox rect={reRect} onChange={setReRect} lockRatio={aspectLock} />
          </div>
        )}
        {count > 1 && !reframe && (
          <button
            className="cs-lb-nav next"
            title="Next (→)"
            onClick={(e) => { stop(e); revealChrome(); go(1); }}
            aria-label="Next"
          >›</button>
        )}
      </div>

      {reframe && (
        <div className="cs-lb-reframe" onClick={stop}>
          <div className="cs-lb-reframe-aspects">
            {CROP_ASPECTS.map((a) => (
              <button
                key={a.id}
                type="button"
                className={`cs-revise-chip${reAspect === a.id ? ' is-on' : ''}`}
                onClick={() => setReAspect(a.id)}
              >
                {a.label}
              </button>
            ))}
          </div>
          <div className="cs-lb-reframe-actions">
            {reMsg && <span className={`cs-revise-note ${reMsg.startsWith('⚠') ? 'err' : 'ok'}`}>{reMsg}</span>}
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setReframe(false)}>Cancel</button>
            <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={reSaving} onClick={applyReframe}>
              {reSaving ? '⏳ Saving…' : '✓ Save reframe'}
            </button>
          </div>
        </div>
      )}

      {count > 1 && !reframe && <div className="cs-lb-hint">Swipe or use ← → · swipe down to close</div>}
      {reframe && <div className="cs-lb-hint">Drag the rectangle · pick an aspect · Save reframe → new version</div>}
    </div>
  );
}
